import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TEST_IDS } from "@/lib/testIds";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignIn() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

// FastAPI 422 returns array-of-objects which crashes React if rendered raw
function formatDetail(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const MODES = { SIGNIN: "signin", SIGNUP: "signup", FORGOT: "forgot" };

export default function AuthDialog({ open, onOpenChange, intent }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState(MODES.SIGNUP);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const finishAuth = (data) => {
    if (data.session_token) localStorage.setItem("nk_session_token", data.session_token);
    setUser(data.user);
    onOpenChange(false);
    const isAdmin = data.user.role === "super_admin" || data.user.role === "sub_admin" || data.user.is_admin;
    if (isAdmin) navigate("/admin");
    else if (!data.user.country || !data.user.username) navigate("/profile-setup");
    else if (intent?.next) navigate(intent.next);
    else navigate("/home");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const trimmedEmail = email.trim().toLowerCase();
    if (mode === MODES.FORGOT) {
      setBusy(true);
      try {
        await api.post("/auth/forgot-password", { email: trimmedEmail });
        toast.success("If that email exists, a reset link has been sent.");
        setMode(MODES.SIGNIN);
      } catch (err) {
        toast.error(formatDetail(err.response?.data?.detail));
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      if (mode === MODES.SIGNUP) {
        const { data } = await api.post("/auth/register", {
          email: trimmedEmail, password, name: name.trim(),
        });
        finishAuth(data);
        toast.success("Welcome to NKognito");
      } else {
        const { data } = await api.post("/auth/login", { email: trimmedEmail, password });
        finishAuth(data);
        toast.success(`Signed in · ${data.user.email}`);
      }
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const heading = mode === MODES.SIGNIN ? "Sign in" :
                  mode === MODES.SIGNUP ? "Create your account" : "Reset your password";
  const subhead = mode === MODES.SIGNIN ? "Welcome back to the arena." :
                  mode === MODES.SIGNUP ? "Free to play — chase Knowledge Rating, Season Score, and your country's pride." :
                  "Enter your email and we'll send you a reset link.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-nk-surface border-nk-line max-w-md p-0 gap-0 rounded-sm"
        aria-describedby={undefined}
      >
        <div className="p-8">
          <div className="eyebrow mb-3">
            {intent?.label ? `Continue to ${intent.label}` : "NKognito"}
          </div>
          <h2 className="font-display text-3xl font-medium tracking-tight text-nk-text mb-2">
            {heading}
          </h2>
          <p className="text-sm text-nk-mute mb-6 leading-relaxed">
            {subhead}
          </p>

          {mode !== MODES.FORGOT && (
            <>
              <button
                data-testid={TEST_IDS.signInGoogleBtn}
                onClick={startGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 bg-nk-text text-nk-bg font-medium px-5 py-3 rounded-sm hover:bg-nk-text/90 transition-colors"
              >
                <GoogleMark />
                Continue with Google
              </button>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-nk-line" />
                <span className="text-xs text-nk-dim uppercase tracking-widest">or</span>
                <div className="flex-1 h-px bg-nk-line" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === MODES.SIGNUP && (
              <input
                data-testid={TEST_IDS.authNameInput}
                required minLength={2} maxLength={80}
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-nk-bg border border-nk-line px-3 py-2.5 text-sm rounded-sm focus:border-nk-accent outline-none transition-colors"
              />
            )}
            <input
              data-testid={TEST_IDS.authEmailInput}
              type="email" required
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-nk-bg border border-nk-line px-3 py-2.5 text-sm rounded-sm focus:border-nk-accent outline-none transition-colors"
              autoComplete="email"
            />
            {mode !== MODES.FORGOT && (
              <input
                data-testid={TEST_IDS.authPasswordInput}
                type="password" required minLength={8}
                placeholder={mode === MODES.SIGNUP ? "Password (8+ chars, letters + numbers)" : "Password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-nk-bg border border-nk-line px-3 py-2.5 text-sm rounded-sm focus:border-nk-accent outline-none transition-colors"
                autoComplete={mode === MODES.SIGNUP ? "new-password" : "current-password"}
              />
            )}
            <button
              data-testid={mode === MODES.SIGNUP ? TEST_IDS.authSignUpBtn : TEST_IDS.authSignInBtn}
              disabled={busy}
              className="w-full bg-nk-accent text-nk-bg font-medium px-4 py-2.5 rounded-sm hover:bg-nk-accentSoft disabled:opacity-40 transition-colors"
            >
              {busy ? "Working..." : mode === MODES.SIGNUP ? "Create account" :
                       mode === MODES.SIGNIN ? "Sign in" : "Send reset link"}
            </button>
          </form>

          <div className="flex items-center justify-between mt-5 text-xs">
            {mode === MODES.SIGNIN && (
              <>
                <button
                  data-testid={TEST_IDS.authSwitchModeBtn}
                  onClick={() => setMode(MODES.SIGNUP)}
                  className="text-nk-mute hover:text-nk-text transition-colors"
                >
                  Need an account? Sign up →
                </button>
                <button
                  data-testid={TEST_IDS.authForgotBtn}
                  onClick={() => setMode(MODES.FORGOT)}
                  className="text-nk-mute hover:text-nk-text transition-colors"
                >
                  Forgot password?
                </button>
              </>
            )}
            {mode === MODES.SIGNUP && (
              <button
                data-testid={TEST_IDS.authSwitchModeBtn}
                onClick={() => setMode(MODES.SIGNIN)}
                className="text-nk-mute hover:text-nk-text transition-colors"
              >
                ← Already a player? Sign in
              </button>
            )}
            {mode === MODES.FORGOT && (
              <button
                onClick={() => setMode(MODES.SIGNIN)}
                className="text-nk-mute hover:text-nk-text transition-colors"
              >
                ← Back to sign in
              </button>
            )}
          </div>

          <p className="text-[11px] text-nk-dim mt-6 leading-relaxed">
            By continuing you agree to fair play and our terms. Free to play — no entry fees, no wallets.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}
